
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bird, LayoutDashboard, Target, Sparkles,
  BarChart3, ArrowRight,
  ShieldCheck, Smartphone, Zap,
  ChevronDown, HelpCircle, Star,
  Award, Sun, Moon, CalendarCheck, GraduationCap
} from 'lucide-react';

interface LandingPageProps {
  onLoginClick: () => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  language: 'pt' | 'en';
  setLanguage: (lang: 'pt' | 'en') => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onLoginClick,
  theme,
  toggleTheme,
  language,
  setLanguage
}) => {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };



  const translations = {
    pt: {
      features: "Funcionalidades",
      faq: "Perguntas Frequentes",
      faqShort: "FAQ",
      login: "Login",
      roadmap: "Roadmap para a Aprovação • 2026",
      heroTitle1: "Mais que organização.",
      heroTitle2: " Um ecossistema de estudos.",
      heroDesc: "O Parthenon Planner é o ecossistema definitivo para organizar sua jornada de estudos e alcançar sua melhor performance em busca dos seus objetivos.",
      startNow: "COMEÇAR AGORA",
      exploreFeatures: "Conhecer Recursos",
      archSuccess: "Arquitetura de Sucesso",
      featureMainTitle: "Tudo o que você precisa em um só ecossistema.",
      sealExcellence: "O Selo de Excelência",
      createdBy: "Um produto criado por ",
      athenaStudies: "Athena Studies.",
      institutionalDesc: "Transformamos a sua jornada de aprendizado por meio de soluções de alta performance. A Athena Studies nasceu para guiar estudantes que buscam autonomia, organização e resultados extraordinários. O Parthenon Planner é a materialização da nossa metodologia pedagógica em formato digital. Venha conhecer mais de nossas soluções e serviços.",
      exploreAthena: "Conhecer Metodologia Athena",
      terms: "Termos de Serviço",
      privacy: "Privacidade",
      masterFuture: "© 2026 • DOMINE O SEU FUTURO",
    },
    en: {
      features: "Features",
      faq: "FAQ",
      faqShort: "FAQ",
      login: "Login",
      roadmap: "Roadmap to Approval • 2026",
      heroTitle1: "More than organization.",
      heroTitle2: " A study ecosystem.",
      heroDesc: "Parthenon Planner is the ultimate ecosystem to organize your study journey and reach your best performance in search of your goals.",
      startNow: "START NOW",
      exploreFeatures: "Explore Features",
      archSuccess: "Architecture of Success",
      featureMainTitle: "Everything you need in a single ecosystem.",
      sealExcellence: "The Seal of Excellence",
      createdBy: "A product created by ",
      athenaStudies: "Athena Studies.",
      institutionalDesc: "We transform your learning journey through high-performance solutions. Athena Studies was born to guide students seeking autonomy, organization, and extraordinary results. Parthenon Planner is the digital embodiment of our pedagogical methodology. Come discover more of our solutions and services.",
      exploreAthena: "Explore Athena Methodology",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      masterFuture: "© 2026 • MASTER YOUR FUTURE",
    }
  };

  const t = translations[language];

  const featureItems = [
    {
      icon: LayoutDashboard,
      title: language === 'pt' ? "Agenda Estratégica" : "Strategic Schedule",
      desc: language === 'pt'
        ? "Organize matérias, tempo dedicado e compromissos em um calendário interativo e funcional."
        : "Organize subjects, dedicated time, and commitments in an interactive and functional calendar.",
      iconColor: "text-emerald-700 dark:text-emerald-400",
      iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
      hoverBg: "group-hover:bg-emerald-600"
    },
    {
      icon: BarChart3,
      title: language === 'pt' ? "Relatórios de Performance" : "Performance Reports",
      desc: language === 'pt'
        ? "Acompanhe sua sequência de estudos, tempo total e evolução semanal com gráficos precisos."
        : "Track your study streak, total time, and weekly progress with precise charts.",
      iconColor: "text-athena-coral",
      iconBg: "bg-athena-coral/20",
      hoverBg: "group-hover:bg-athena-coral"
    },
    {
      icon: Sparkles,
      title: language === 'pt' ? "Insights Inteligentes" : "Smart Insights",
      desc: language === 'pt'
        ? "Receba notificações sobre seu ritmo de estudo e alertas de foco para nunca perder a constância."
        : "Receive notifications about your study pace and focus alerts to never lose consistency.",
      iconColor: "text-athena-teal",
      iconBg: "bg-athena-teal/20",
      hoverBg: "group-hover:bg-athena-teal"
    },
    {
      icon: Smartphone,
      title: language === 'pt' ? "Mobilidade Sem Limites" : "Limitless Mobility",
      desc: language === 'pt'
        ? "Acesse sua jornada em qualquer dispositivo com sincronização total e integração direta com o Google Calendar para compromissos e sessões de estudo."
        : "Access your journey on any device with total synchronization and direct integration with Google Calendar for commitments and study sessions.",
      iconColor: "text-purple-700 dark:text-purple-400",
      iconBg: "bg-purple-100 dark:bg-purple-900/40",
      hoverBg: "group-hover:bg-purple-600"
    },
    {
      icon: Award,
      title: language === 'pt' ? "Conexão Athena" : "Athena Connection",
      desc: language === 'pt'
        ? "Compatibilidade total com os serviços da Athena Studies. Integre seus Study Planners Personalizados, mentorias e listas de exercícios para um suporte pedagógico de elite."
        : "Full compatibility with Athena Studies services. Integrate your Personalized Study Planners, mentoring, and exercise sheets for elite pedagogical support.",
      iconColor: "text-blue-700 dark:text-blue-400",
      iconBg: "bg-blue-100 dark:bg-blue-900/40",
      hoverBg: "group-hover:bg-blue-700"
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-sans selection:bg-athena-coral selection:text-white scroll-smooth transition-colors duration-300">

      {/* Navigation Header */}
      <header className="fixed top-0 w-full z-[100] backdrop-blur-xl bg-white/90 dark:bg-slate-900/90 border-b border-slate-300 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="h-16 md:h-20 flex items-center justify-between">
            <button
              onClick={(e) => scrollToSection(e as any, 'top')}
              className="flex items-center gap-2 group"
            >
              <div className="p-1.5 md:p-2 bg-athena-coral rounded-lg md:rounded-xl text-white shadow-lg group-hover:scale-110 transition-transform">
                <Bird size={20} className="md:size-6" />
              </div>
              <h1 className="text-lg md:text-xl font-black tracking-tighter text-athena-teal dark:text-white uppercase leading-none">
                Parthenon<span className="text-athena-coral">.</span>
              </h1>
            </button>

            <nav className="hidden lg:flex items-center gap-10">
              <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="text-sm font-black uppercase text-slate-800 dark:text-slate-200 hover:text-athena-coral tracking-widest transition-colors">{t.features}</a>
              <Link to="/faq" className="text-sm font-black uppercase text-slate-800 dark:text-slate-200 hover:text-athena-coral tracking-widest transition-colors">{t.faq}</Link>
            </nav>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setLanguage('pt')}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${language === 'pt'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  title="Português"
                >
                  <img src="https://flagcdn.com/br.svg" alt="Português" className="w-5 h-5 rounded-full object-cover" />
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${language === 'en'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  title="English"
                >
                  <img src="https://flagcdn.com/us.svg" alt="English" className="w-5 h-5 rounded-full object-cover" />
                </button>
              </div>

              <button
                onClick={toggleTheme}
                className="p-2 md:p-2.5 rounded-lg md:rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border border-slate-300 dark:border-slate-700 shadow-sm hover:scale-110 active:scale-95 transition-all"
                title={language === 'pt' ? "Trocar Tema" : "Toggle Theme"}
              >
                {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
              </button>
              <button
                onClick={onLoginClick}
                className="px-4 md:px-8 py-2 md:py-3 bg-athena-teal text-white rounded-lg md:rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-athena-teal/90 active:scale-95 transition-all flex items-center gap-2 border-b-4 border-slate-900/20"
              >
                {t.login} <ArrowRight size={12} className="hidden md:inline" />
              </button>
            </div>
          </div>

          <nav className="lg:hidden flex items-center justify-center gap-6 py-2 border-t border-slate-100 dark:border-slate-800 overflow-x-auto no-scrollbar">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="whitespace-nowrap text-[10px] font-black uppercase text-slate-700 dark:text-slate-300 tracking-wider">{t.features}</a>
            <Link to="/faq" className="whitespace-nowrap text-[10px] font-black uppercase text-slate-700 dark:text-slate-300 tracking-wider">{t.faqShort}</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 md:pt-48 pb-20 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">
          {/* Hero Text */}
          <div className="w-full md:w-1/2 space-y-8 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-athena-coral/20 text-athena-coral rounded-full border border-athena-coral/40 animate-fade-in">
              <Sparkles size={14} />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t.roadmap}</span>
            </div>

            <h2 className="text-4xl md:text-6xl font-black leading-[1.1] tracking-tighter text-slate-950 dark:text-white">
              <span className="text-athena-coral">{t.heroTitle1}</span> <span className="text-athena-teal">{t.heroTitle2}</span>
            </h2>

            <p className="text-lg md:text-xl font-bold text-slate-700 dark:text-slate-400 max-w-xl mx-auto md:mx-0 leading-relaxed">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 pt-6">
              <button
                onClick={onLoginClick}
                className="w-full sm:w-auto px-12 py-5 bg-amber-500 text-slate-950 rounded-2xl font-black uppercase tracking-widest shadow-2xl hover:bg-amber-400 active:scale-95 transition-all flex items-center justify-center gap-3 text-sm border-b-4 border-amber-600"
              >
                {t.startNow} <Zap size={20} />
              </button>
              <a
                href="#features"
                onClick={(e) => scrollToSection(e, 'features')}
                className="w-full sm:w-auto px-12 py-5 bg-white dark:bg-slate-900 text-slate-950 dark:text-white rounded-2xl font-black uppercase tracking-widest shadow-xl border-2 border-slate-400 dark:border-slate-800 hover:bg-slate-50 transition-all text-sm"
              >
                {t.exploreFeatures}
              </a>
            </div>
          </div>

          {/* Hero Photo */}
          <div className="w-full md:w-1/2 flex justify-center md:justify-end animate-fade-in">
            <div className="relative group">
              <div className="absolute -inset-6 bg-athena-coral/10 rounded-[4rem] blur-3xl group-hover:bg-athena-coral/20 transition-all duration-500"></div>
              <div className="relative rounded-[3rem] shadow-[0_32px_64px_-15px_rgba(0,0,0,0.25)] overflow-hidden aspect-[5/4] w-full max-w-[520px] border-2 border-slate-200 dark:border-slate-800">
                <img
                  src="https://i.imgur.com/aE2mKLZ.jpeg"
                  alt="Parthenon Planner"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-32 px-6 scroll-mt-24 bg-slate-50 dark:bg-slate-950/40">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-20">
            <h3 className="text-[11px] font-black text-athena-teal uppercase tracking-[0.4em]">{t.archSuccess}</h3>
            <h2 className="text-4xl md:text-5xl font-black text-slate-950 dark:text-white tracking-tighter">{t.featureMainTitle}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featureItems.map((feature, i) => (
              <div key={i} className="group p-10 bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-800 rounded-[2.5rem] hover:border-athena-coral hover:shadow-2xl transition-all duration-300 shadow-md">
                <div className={`w-16 h-16 ${feature.iconBg} ${feature.iconColor} rounded-2xl flex items-center justify-center mb-8 ${feature.hoverBg} group-hover:text-white transition-all duration-300 border border-slate-200 dark:border-slate-700`}>
                  <feature.icon size={32} />
                </div>
                <h4 className="text-xl font-black text-slate-950 dark:text-white mb-4 uppercase tracking-tighter">{feature.title}</h4>
                <p className="text-sm font-bold text-slate-800 dark:text-slate-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section: Athena Studies Institutional */}
      <section className="py-32 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16 md:gap-24">
          <div className="w-full md:w-1/2 flex justify-center md:justify-end animate-fade-in">
            <div className="relative group">
              <div className="absolute -inset-6 bg-athena-teal/10 rounded-[4rem] blur-3xl group-hover:bg-athena-teal/20 transition-all duration-500"></div>
              <div className="relative p-0 bg-transparent rounded-[3rem] shadow-[0_32px_64px_-15px_rgba(0,0,0,0.2)] overflow-hidden flex items-center justify-center aspect-square max-w-[320px]">
                <img
                  src="https://i.imgur.com/SD28FYh.png"
                  alt="Athena Studies Logo"
                  className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 space-y-8 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-athena-teal/10 text-athena-teal rounded-full border border-athena-teal/20">
              <Award size={14} />
              <span className="text-[10px] font-black uppercase tracking-widest">{t.sealExcellence}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-950 dark:text-white tracking-tighter leading-none">
              {t.createdBy}<span className="text-athena-teal">{t.athenaStudies}</span>
            </h2>
            <p className="text-lg font-bold text-slate-700 dark:text-slate-400 leading-relaxed">
              {t.institutionalDesc}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6 justify-center md:justify-start">
              <a
                href="https://athenastudies.vercel.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-athena-teal hover:text-athena-coral font-black uppercase text-[10px] tracking-widest transition-all group border-2 border-athena-teal/20 px-5 py-2.5 rounded-xl hover:border-athena-coral/20 hover:bg-slate-50 dark:hover:bg-slate-900"
              >
                {t.exploreAthena} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-inner">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10">
          <button
            onClick={(e) => scrollToSection(e as any, 'top')}
            className="flex items-center gap-3 group"
          >
            <div className="p-2 bg-athena-coral rounded-xl text-white shadow-lg group-hover:scale-110 transition-transform">
              <Bird size={24} />
            </div>
            <h1 className="text-xl font-black tracking-tighter text-athena-teal dark:text-white uppercase">Parthenon</h1>
          </button>

          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            <Link
              to="/termos"
              className="text-[13px] md:text-sm font-black uppercase text-slate-700 dark:text-slate-300 tracking-widest hover:text-athena-teal transition-colors"
            >
              {t.terms}
            </Link>
            <Link
              to="/privacidade"
              className="text-[13px] md:text-sm font-black uppercase text-slate-700 dark:text-slate-300 tracking-widest hover:text-athena-teal transition-colors"
            >
              {t.privacy}
            </Link>
            <Link
              to="/faq"
              className="text-[13px] md:text-sm font-black uppercase text-slate-700 dark:text-slate-300 tracking-widest hover:text-athena-teal transition-colors"
            >
              {t.faq}
            </Link>
          </div>

          <p className="text-[11px] font-black uppercase text-slate-500 tracking-widest">{t.masterFuture}</p>
        </div>
      </footer>
    </div>
  );
};
